import { useState, useEffect, useMemo, useContext } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// @mui material components components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Kit 2 React themes
import theme from "assets/theme";
import Presentation from "layouts/pages/presentation";

import { AuthContext } from "context";
import { routes, routesToExcludeFromNavbar } from "routes";

import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";
import Login from "auth/login";
import Register from "auth/register";

import Details from "pages/Details";
import Profile from "pages/Profile";
import Dashboard from "pages/Dashboard";
import Order from "pages/Order";
import Claim from "pages/Claim";
import Checkout from "pages/Checkout";

// Health
import SeniorsHealthPlans from "pages/Health/SeniorsHealthPlans";
import IndividualHealthPlans from "pages/Health/IndividualHealthPlans";
import FamilyHealthPlans from "pages/Health/FamilyHealthPlans";
import JuniorHealthPlans from "pages/Health/JuniorHealthPlans";

// Motor
import Comprehensive from "pages/Motor/Comprehensive";
import ThirdPartyOnly from "pages/Motor/ThirdPartyOnly";
import MotorCycleInsurance from "pages/Motor/MotorCycleInsurance";
import PsvBodaBoda from "pages/Motor/PsvBodaBoda";
import MotorPsv from "pages/Motor/MotorPsv";

// Life
import LifeInsurance from "pages/Life/LifeInsurance";
import EducationInsurance from "pages/Life/EducationInsurance";

// Other
import HomeInsurance from "pages/Other/HomeInsurance";
import PersonalAccidentInsurance from "pages/Other/PersonalAccidentInsurance";
import ProfessionalIndemnityInsurance from "pages/Other/ProfessionalIndemnityInsurance";
import FireAndBurglary from "pages/Other/FireAndBurglary";
import LastExpense from "pages/Other/LastExpense";
import InsurancePremiumFinancing from "pages/Other/InsurancePremiumFinancing";

import AboutUs from "pages/LandingPages/AboutUs";
import Author from "pages/LandingPages/Author";
import ContactUs from "pages/LandingPages/ContactUs";

import MKBox from "components/MKBox";

import { setupAxiosInterceptors } from "./services/interceptor";
import ProtectedRoute from "examples/ProtectedRoute";
import Loading from "examples/Loading";
import Notifications from "examples/Notifications";

export default function App() {
  const authContext = useContext(AuthContext);
  const { pathname } = useLocation();

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();
  setupAxiosInterceptors(() => {
    authContext.logout();
    navigate("/auth/login");
  });

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route && route.type !== "auth") {
          return (
            <Route
              exact
              path={route.route}
              element={
                <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                  {route.component}
                </ProtectedRoute>
              }
              key={route.key}
            />
          );
        }

        return null;
      })
      .concat(
        routesToExcludeFromNavbar.map((route) => (
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        ))
      );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MKBox>
        <Loading />
      </MKBox>
      <MKBox>
        <Notifications />
      </MKBox>
      <Routes>
        <Route path="/auth/login" element={<Login />} key="login" />
        <Route path="/auth/register" element={<Register />} key="register" />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />

        <Route exact path="/presentation" element={<Presentation />} />
        <Route
          exact
          path="/health/seniors_health_plans"
          element={<SeniorsHealthPlans />}
        />
        <Route
          exact
          path="/health/individual_health_plans"
          element={<IndividualHealthPlans />}
        />
        <Route
          exact
          path="/health/family_health_plans"
          element={<FamilyHealthPlans />}
        />
        <Route
          exact
          path="/health/junior_health_plans"
          element={<JuniorHealthPlans />}
        />
        <Route exact path="/motor/comprehensive" element={<Comprehensive />} />
        <Route
          exact
          path="/motor/third_party_only"
          element={<ThirdPartyOnly />}
        />
        <Route
          exact
          path="/motor/motor_cycle_insurance"
          element={<MotorCycleInsurance />}
        />
        <Route exact path="/motor/psv_boda_boda" element={<PsvBodaBoda />} />
        <Route exact path="/motor/motor_psv" element={<MotorPsv />} />
        <Route exact path="/life/life_insurance" element={<LifeInsurance />} />
        <Route
          exact
          path="/life/education_insurance"
          element={<EducationInsurance />}
        />
        <Route exact path="/other/home_insurance" element={<HomeInsurance />} />
        <Route
          exact
          path="/other/personal_accident_insurance"
          element={<PersonalAccidentInsurance />}
        />
        <Route
          exact
          path="/other/professional_indemnity_insurance"
          element={<ProfessionalIndemnityInsurance />}
        />
        <Route
          exact
          path="/other/fire_and_burglary"
          element={<FireAndBurglary />}
        />
        <Route exact path="/other/last_expense" element={<LastExpense />} />
        <Route
          exact
          path="/other/insurance_premium_financing"
          element={<InsurancePremiumFinancing />}
        />
        {/* <Route
          exact
          path="/pages/landing-pages/about-us"
          element={<AboutUs />}
        /> */}
        {/* <Route exact path="/pages/landing-pages/author" element={<Author />} /> */}
        {/* <Route exact path="/pages/landing-pages/author2" element={<Author />} />
        <Route
          exact
          path="/pages/landing-pages/contact-us"
          element={<ContactUs />}
        />
        <Route
          exact
          path="/pages/landing-pages/contact-us2"
          element={<ContactUs />}
        /> */}
        <Route exact path="/details/:id" element={<Details />} key="details" />

        <Route
          exact
          path="/dashboard"
          element={
            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
              <Dashboard />
            </ProtectedRoute>
          }
          key="dashboard"
        />
        <Route
          exact
          path="/profile"
          element={
            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
              <Profile />
            </ProtectedRoute>
          }
          key="profile"
        />
        <Route
          exact
          path="/order/:id"
          element={
            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
              <Order />
            </ProtectedRoute>
          }
          key="order"
        />
        <Route
          exact
          path="/claim/:id"
          element={
            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
              <Claim />
            </ProtectedRoute>
          }
          key="claim"
        />
        <Route
          exact
          path="/checkout/:id"
          element={
            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
              <Checkout />
            </ProtectedRoute>
          }
          key="checkout"
        />

        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/presentation" />} />
      </Routes>
    </ThemeProvider>
  );
}
